import React from 'react'
import Select from 'react-select'
import {
  RAIL_KIND_LABELS,
  RAIL_KIND_OPTIONS,
  RailKind,
  RailKindOption,
  inferRailKind,
} from '../../../constants/railKinds'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata } from './CarouselEditor'

interface CarouselRailKindFieldProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

/**
 * The rail kind picker: what this carousel is for. Stored as
 * metadata.rail_kind. Dynamic carousel ordering uses it to rank the rail when
 * ACE's rail_order_enabled flag is on; while the flag is off, nothing reads it.
 *
 * - A suggestion from the filters is saved only when the editor clicks
 *   "Use it", so every stored value is an editor's choice.
 * - A set value is never changed. If the filters suggest a different kind,
 *   the editor sees a note.
 * - Empty is valid: the rail is then ranked on what it returns, which suits a
 *   rail that mixes kinds.
 */
const CarouselRailKindField: React.FC<CarouselRailKindFieldProps> = ({ metadata, onChange }) => {
  const current = typeof metadata.rail_kind === 'string' ? metadata.rail_kind : ''
  const suggestion = inferRailKind(metadata)

  const set = (value: RailKind | undefined) => {
    const next: CarouselMetadata = { ...metadata, rail_kind: value }
    if (!value) delete next.rail_kind
    onChange(next)
  }

  const selected = RAIL_KIND_OPTIONS.find(o => o.value === current) ?? null
  const disagrees = Boolean(current) && Boolean(suggestion) && suggestion !== current

  return (
    <div className={formStyles.fieldGroup}>
      <label htmlFor="rail-kind">What this rail is for</label>
      <Select<RailKindOption>
        inputId="rail-kind"
        isClearable
        options={RAIL_KIND_OPTIONS}
        value={selected}
        getOptionLabel={(o) => o.label}
        getOptionValue={(o) => o.value}
        onChange={(o) => set(o?.value)}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
        placeholder="Optional"
        aria-label="What this rail is for"
      />

      <p className={formStyles.hint}>
        Used to place this row on the page when dynamic carousel ordering is on.
        Until then, rows keep the order set on this page.
      </p>

      {selected?.hint && <p className={formStyles.hint}>{selected.hint}</p>}

      {!current && suggestion && (
        <p className={formStyles.hint}>
          These filters look like <strong>{RAIL_KIND_LABELS[suggestion]}</strong>.{' '}
          <button type="button" className={formStyles.linkButton} onClick={() => set(suggestion)}>
            Use it
          </button>
        </p>
      )}

      {!current && !suggestion && (
        <p className={formStyles.hint}>
          Left empty, dynamic ordering ranks this rail on what it contains.
        </p>
      )}

      {disagrees && (
        <p className={formStyles.hint}>
          The filters look more like <strong>{RAIL_KIND_LABELS[suggestion as string]}</strong>.
          Your choice is kept.
        </p>
      )}
    </div>
  )
}

export default CarouselRailKindField
